import Big from 'big.js'
import { useMemo } from 'react'

import { bigFromDecimal, bigFromFee } from 'utils/price/decimals'
import { useEmbedConfig } from 'components/pages/PurchaseFlow/_main/contexts/EmbedConfig.context'
import { usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts/PurchaseFlowProducts.context'
import { formatPercentage, valueMultiplied } from 'utils/price'
import { useFormatterLanguage } from 'utils/hooks/useFormatterLanguage'

/**
 * Derives the OneSystem cart summary (green contract banner + Zwischensumme/Partnerrabatt lines) from the
 * currently selected products. Embed-only: outside the Propstack embed `isOneSystemCart` is always false so
 * the non-embed cart renders byte-identically.
 *
 * Covered ⇔ `feePrice == 0 && originalPrice > 0`; charged ⇔ `feePrice > 0`. All comparisons are on Big
 * values, never on formatted-price strings. The pricing itself (greedy quota allocation) is server-owned;
 * this hook only reads the repriced lines.
 */
export const useEmbedOneSystemSummary = () => {
  const { isEmbed } = useEmbedConfig()
  const { selectedProducts } = usePurchaseFlowProducts()
  // formatPercentage follows the app language; without this dep the label keeps the old separator after a language switch
  const formatterLanguage = useFormatterLanguage()

  return useMemo(() => {
    let hasCoveredLine = false
    let hasChannelDiscountLine = false
    let zwischensumme = Big(0)
    let partnerrabatt = Big(0)

    for (const product of Object.values(selectedProducts)) {
      const fee = bigFromFee(product.feePrice)
      const original = product.originalPrice ? bigFromDecimal(product.originalPrice.value) : null
      const quantity = product.quantity ?? 1

      const isCovered = fee.eq(0) && !!original && original.gt(0)
      const isCharged = fee.gt(0)

      if (isCovered) hasCoveredLine = true
      if (!!original && original.gt(fee)) hasChannelDiscountLine = true

      // Zwischensumme/Partnerrabatt accumulate over CHARGED lines only (covered lines contribute 0).
      if (!isCharged) continue

      const reference = original ?? fee
      zwischensumme = zwischensumme.plus(valueMultiplied(reference, quantity))
      partnerrabatt = partnerrabatt.plus(valueMultiplied(reference.minus(fee), quantity))
    }

    // Embed-gate the covered flag too so the banner can never leak into the non-embed cart
    // (covered lines are channel-priced and thus embed-only by construction, but be explicit).
    const isCoveredInEmbed = isEmbed && hasCoveredLine
    const isOneSystemCart = isEmbed && (hasCoveredLine || hasChannelDiscountLine)

    // Locale-aware like every other percent label (ISSUE-3364); the contract rate is 8.5 % when not computable
    const percentageLabel = formatPercentage(zwischensumme.gt(0) ? partnerrabatt.div(zwischensumme).times(100) : Big('8.5'), formatterLanguage)

    return { isOneSystemCart, hasCoveredLine: isCoveredInEmbed, zwischensumme, partnerrabatt, percentageLabel }
  }, [isEmbed, selectedProducts, formatterLanguage])
}
